<template>
  <PageChrome :show-header="false">
    <div class="playDetail" v-if="play">
      <SalesPlayHeader
        :play-id="playId"
        :play="play"
        :agent-run-counts="{ running: liveAgentRef.value, queued: 0 }"
        :refresh-play="() => fetchPlay(true)"
        @stage-filter="(stage, kind) => $router.push(`/sales-plays/${playId}/candidates/${kind === 'account' ? 'companies' : 'people'}${stage ? `?stage=${stage}` : ''}`)"
      />

      <SalesPlayTabBar :play-id="playId" :engine="play?.engine" />

      <div class="grid">
        <SalesPlayCard
          title="Candidates"
          @click="goto('candidates')"
        >
          <ul class="candidateLifecycle">
            <li v-if="play.pending_review_count > 0" class="lcRow">
              <span class="lcDot lcDotPending" />
              <span class="lcLabel">Pending approval</span>
              <span class="lcValue">{{ play.pending_review_count }}</span>
            </li>
            <li class="lcRow">
              <span class="lcDot lcDotNew" />
              <span class="lcLabel">Qualified</span>
              <span class="lcValue">{{ play.new_member_count ?? 0 }}</span>
            </li>
            <li v-if="inSequenceCount > 0" class="lcRow">
              <span class="lcDot lcDotActive" />
              <span class="lcLabel">In sequence</span>
              <span class="lcValue">{{ inSequenceCount }}</span>
            </li>
            <li v-if="play.engaged_count > 0" class="lcRow">
              <span class="lcDot lcDotEngaged" />
              <span class="lcLabel">Engaged · {{ play.engaged_pct }}%</span>
              <span class="lcValue">{{ play.engaged_count }}</span>
            </li>
            <li class="lcRow lcRowTotal">
              <span class="lcLabel">Total</span>
              <span class="lcValue">{{ candidateTotal }}</span>
            </li>
          </ul>
        </SalesPlayCard>

        <SalesPlayCard
          title="Find"
          :badge="(play.sources || []).length ? `${play.sources.length}` : 'None'"
          :badge-tone="(play.sources || []).length ? 'built' : 'empty'"
          @click="$router.push(`/sales-plays/${playId}`)"
        >
          <div v-if="!(play.sources || []).length" class="pipelineEmpty">
            Not configured &mdash; click to set up on the canvas
          </div>
          <div v-for="s in (play.sources || [])" :key="s.id" class="pipelineRow">
            <div class="pipelineRowBody">
              <div class="pipelineName">{{ s.source_type }}</div>
              <div class="pipelineRowMeta">
                <span class="pipelineStatusPill" :class="`status-${s.status}`">
                  {{ s.status === 'active' ? 'Enabled' : s.status }}
                </span>
                <span v-if="s.version" class="pipelineVersionPill">v{{ s.version }}</span>
              </div>
            </div>
          </div>
        </SalesPlayCard>

        <SalesPlayCard
          title="ICP"
          :badge="hasIcp ? 'Built' : 'Not configured'"
          :badge-tone="hasIcp ? 'built' : 'empty'"
          :version-label="icpVersionLabel"
          @click="goto('icp')"
        >
          <dl v-if="hasIcp" class="icpList">
            <template v-for="(value, key) in icpEntries" :key="key">
              <dt>{{ formatIcpKey(String(key)) }}</dt>
              <dd>
                <template v-if="Array.isArray(value)">
                  <span v-for="(v, idx) in value" :key="idx" class="chip">{{ v }}</span>
                </template>
                <template v-else>{{ value }}</template>
              </dd>
            </template>
          </dl>
        </SalesPlayCard>

        <SalesPlayCard
          title="Personas"
          :badge="hasPersonas ? 'Built' : 'Not configured'"
          :badge-tone="hasPersonas ? 'built' : 'empty'"
          @click="goto('personas')"
        >
          <ul v-if="hasPersonas" class="personaList">
            <li v-for="p in personaList" :key="p.id" class="personaRow">
              <span class="personaInitials">{{ personaInitials(p.name) }}</span>
              <div class="personaText">
                <span class="personaName">{{ p.name }}</span>
                <span class="personaMeta">{{ (p.explicit_titles || []).join(', ') || '—' }}</span>
              </div>
            </li>
          </ul>
        </SalesPlayCard>

        <SalesPlayCard
          title="Sequence"
          :badge="sequenceReady ? 'Built' : 'Not configured'"
          :badge-tone="sequenceReady ? 'built' : 'empty'"
          :version-label="sequenceVersionLabel"
          span-cols="2"
          @click="goto('sequence')"
        >
          <div v-if="sequenceReady" class="sequenceRow">
            <div v-for="step in play.sequence_step_counts" :key="step.step_index" class="sequenceStep">
              <AppIcon class="sequenceStepIcon" :name="iconForStepType(step.step_type)" :size="16" />
              <span class="sequenceStepCount">{{ step.count }}</span>
            </div>
          </div>
        </SalesPlayCard>

        <SalesPlayCard
          title="Agent"
          :badge="agentReady ? 'Built' : 'Not configured'"
          :badge-tone="agentReady ? 'built' : 'empty'"
          @click="goto('agent')"
        >
          <p v-if="agentReady" class="approachText">{{ play.prospecting_agent?.approach }}</p>
          <button v-if="agentReady" class="activityLink" type="button" @click.stop="goto('activity')">
            View activity
            <AppIcon name="arrow_right" :size="12" />
          </button>
        </SalesPlayCard>

        <SalesPlayCard
          title="Sample email"
          :badge="hasSampleEmail ? 'Built' : 'Not configured'"
          :badge-tone="hasSampleEmail ? 'built' : 'empty'"
          span-cols="2"
          @click="goto('emails')"
        >
          <div v-if="hasSampleEmail" class="emailBlock">
            <div class="emailPreview">{{ sampleEmailPreview }}</div>
          </div>
        </SalesPlayCard>

      </div>

    </div>

    <div v-else-if="loading" class="loading">Loading play…</div>
    <div v-else class="error">{{ error || 'Play not found.' }}</div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import SalesPlayCard from '../components/SalesPlayCard.vue'
import SalesPlayHeader from '../components/SalesPlayHeader.vue'
import type { PlayEngine } from '../composables/usePlayEngine'
import SalesPlayTabBar from '../components/SalesPlayTabBar.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'
import { runningAgentCountForPlay } from '../../services/actionCountsPoller'

interface SequenceStepCount {
  step_index: number
  step_type: string
  count: number
}

interface PersonaRow {
  id: string
  name: string
  description?: string
  explicit_titles?: string[]
  titles_to_exclude?: string[]
}

interface SalesPlayAction {
  id: string
  action_type: 'sequence' | 'dialer'
  tier: number
  status: string
  current_version: number
  definition: Record<string, any>
}

interface PlayDetail {
  id: string
  name: string
  description: string | null
  status: string
  engine?: PlayEngine
  created_by: { id: string; email: string; name: string } | null
  prospecting_agent: { id: string; approach: string | null; status: string } | null
  actions: SalesPlayAction[]
  icp: { id: string; current_version: number; config: Record<string, unknown>; updated_at: string } | null
  personas: { local: PersonaRow[]; referenced: PersonaRow[] }
  active_account_count: number
  active_contact_count: number
  new_member_count?: number
  pending_review_count: number
  qualified_count?: number
  active_count?: number
  engaged_count: number
  timed_out_count?: number
  disqualified_count?: number
  pending_reachouts_count: number
  pending_human_dials_count: number
  active_agent_count: number
  engaged_pct: number
  total_enrolled_count: number
  sequence_step_counts: SequenceStepCount[]
  sources?: { id: string; source_type: string; status: string; version: number }[]
  filters?: { id: string; filter_type: string; sort_order: number }[]
  // Lifecycle-redesign config (migration 0053).
  auto_approval_policy?: string | null
  require_enrichment_for_auto_approve?: boolean
  auto_run_prospecting?: string | null
  auto_run_action_phase?: boolean
  ownership_config?: Record<string, any> | null
}

const route = useRoute()
const router = useRouter()
const { setScope, clearScope } = useChatScope()

const play = ref<PlayDetail | null>(null)
const loading = ref(true)
const error = ref('')

const playId = computed(() => String(route.params.id || ''))

const liveAgentRef = computed(() => runningAgentCountForPlay(playId.value))

const activeCount = computed(() => {
  if (!play.value) return 0
  return (play.value.active_account_count || 0) + (play.value.active_contact_count || 0)
})
const inSequenceCount = computed(() =>
  (play.value?.sequence_step_counts || []).reduce((sum: number, s: any) => sum + (s.count || 0), 0),
)
const candidateTotal = computed(() => {
  if (!play.value) return 0
  return (play.value.pending_review_count || 0)
    + activeCount.value
    + (play.value.total_enrolled_count || 0)
})

const hasIcp = computed(() => {
  const cfg = play.value?.icp?.config
  return !!cfg && typeof cfg === 'object' && Object.keys(cfg).length > 0
})
const personaList = computed<PersonaRow[]>(() => {
  const p = play.value?.personas
  if (!p) return []
  return [...(p.local || []), ...(p.referenced || [])]
})
const hasPersonas = computed(() => personaList.value.length > 0)
const sequenceAction = computed(() =>
  play.value?.actions?.find((a) => a.action_type === 'sequence') || null,
)
const sampleEmails = computed(() => sequenceAction.value?.definition?.sample_emails || [])
const hasSampleEmail = computed(() => sampleEmails.value.length > 0)
const agentReady = computed(() => Boolean(play.value?.prospecting_agent?.approach))
const sequenceReady = computed(() => (sequenceAction.value?.current_version ?? 0) > 0)
const icpVersionLabel = computed(() =>
  play.value?.icp?.current_version != null ? `v${play.value.icp.current_version}` : '',
)
const sequenceVersionLabel = computed(() =>
  sequenceAction.value?.current_version != null ? `v${sequenceAction.value.current_version}` : '',
)

const icpEntries = computed<Record<string, unknown>>(() => {
  const cfg = play.value?.icp?.config
  if (!cfg || typeof cfg !== 'object') return {}
  return cfg as Record<string, unknown>
})

const sampleEmailPreview = computed(() => {
  const body = sampleEmails.value[0]?.body || ''
  return body.length > 280 ? body.slice(0, 280) + '…' : body
})


function formatRelativeTime(ts: string | null): string {
  if (!ts) return ''
  try {
    const ms = Date.now() - new Date(ts).getTime()
    if (ms < 60_000) return 'just now'
    if (ms < 3600_000) return `${Math.round(ms / 60_000)}m ago`
    if (ms < 86400_000) return `${Math.round(ms / 3600_000)}h ago`
    return `${Math.round(ms / 86400_000)}d ago`
  } catch { return '' }
}

function formatIcpKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function personaInitials(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '·'
  return parts.slice(0, 2).map((s) => s[0].toUpperCase()).join('')
}

function iconForStepType(step_type: string): string {
  switch (step_type) {
    case 'email': return 'email'
    case 'linkedin_connect': return 'linkedin'
    case 'voicemail': return 'voicemail'
    case 'wait': return 'wait'
    default: return 'sequence'
  }
}

function goto(section: 'agent' | 'sequence' | 'icp' | 'personas' | 'candidates' | 'emails' | 'activity') {
  if (section === 'candidates') {
    router.push(`/sales-plays/${playId.value}/candidates/people`)
    return
  }
  router.push(`/sales-plays/${playId.value}/${section}`)
}

let pollHandle: number | undefined

async function fetchPlay(silent = false) {
  if (!silent) {
    loading.value = true
    error.value = ''
  }
  try {
    const resp: any = await apiFetch(`/sales-plays/${playId.value}`)
    play.value = resp?.play || null
    if (play.value?.engine === 'flock') await router.replace(`/sales-plays/${playId.value}`)
    if (silent) error.value = ''
  } catch (e: any) {
    if (!silent) error.value = e?.message || 'Failed to load play'
  } finally {
    if (!silent) loading.value = false
  }
}

onMounted(async () => {
  setScope({
    scopeType: 'sales_play',
    scopeSubjectId: playId.value,
    pageContext: { sales_play_id: playId.value },
  })
  await fetchPlay(false)
  pollHandle = window.setInterval(() => fetchPlay(true), 2000)
})

onBeforeUnmount(() => {
  if (pollHandle !== undefined) window.clearInterval(pollHandle)
  clearScope()
})
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
}

.loading, .error {
  margin-top: var(--space-7);
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  color: var(--color-text-secondary);
}

/* ─── Candidates lifecycle ─── */
.candidateLifecycle { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-2); }
.lcRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.lcDot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  flex-shrink: 0;
}
.lcDotPending { background: var(--color-warning, #f59e0b); }
.lcDotNew { background: var(--color-text-tertiary); }
.lcDotActive { background: var(--color-accent); }
.lcDotEngaged { background: var(--color-accent); opacity: 0.5; }
.lcLabel {
  flex: 1;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}
.lcValue {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  font-variant-numeric: tabular-nums;
}
.lcRowTotal {
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-border-subtle);
  margin-top: var(--space-1);
}
.lcRowTotal .lcLabel { font-weight: var(--fw-semibold); color: var(--color-text-primary); }

/* ─── Pipelines card ─── */
.pipelineEmpty {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
}
.pipelineRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  transition: background var(--duration-fast) var(--ease-out);
}
.pipelineRow:hover { background: var(--color-bg-subtle); }
.pipelineRow + .pipelineRow { border-top: 1px solid var(--color-border-subtle); border-radius: 0; }
.pipelineRow:last-child { border-radius: 0 0 var(--radius-md) var(--radius-md); }
.pipelineRow:first-child { border-radius: var(--radius-md) var(--radius-md) 0 0; }
.pipelineRow:only-child { border-radius: var(--radius-md); }
.pipelineRowBody { flex: 1; min-width: 0; }
.pipelineName {
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pipelineRowMeta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
  margin-top: 2px;
}
.pipelineVersionPill {
  padding: 1px var(--space-2);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-full);
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
}
.pipelineLastRun {
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}
.pipelineRunSummary {
  font-size: var(--fs-xs);
  color: var(--color-text-secondary);
}
.pipelineRowArrow {
  flex-shrink: 0;
  color: var(--color-text-tertiary);
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-out);
}
.pipelineRow:hover .pipelineRowArrow { opacity: 1; }
.pipelineStatusPill {
  padding: 1px var(--space-2);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-full);
  text-transform: lowercase;
}
.pipelineStatusPill.status-active {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}
.pipelineStatusPill.status-paused {
  background: var(--color-warning-soft, var(--color-bg-muted));
  color: var(--color-warning, var(--color-text-secondary));
}
.pipelineStatusPill.status-archived {
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
}

/* ─── ICP ─── */
.icpList {
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  gap: var(--space-2) var(--space-3);
  margin: 0;
}
.icpList dt {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-regular);
  color: var(--color-text-tertiary);
}
.icpList dd {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
}
.chip {
  padding: 2px var(--space-2);
  background: var(--color-bg-subtle);
  border-radius: var(--radius-sm);
  font-size: var(--fs-xs);
  color: var(--color-text-primary);
}

/* ─── Personas ─── */
.personaList { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-2); }
.personaRow { display: flex; align-items: center; gap: var(--space-3); }
.personaInitials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-full);
  background: var(--color-bg-subtle);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  flex-shrink: 0;
}
.personaText { display: flex; flex-direction: column; min-width: 0; }
.personaName {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.personaMeta {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}

/* ─── Sequence ─── */
.sequenceRow {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}
.sequenceStep {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  min-width: 36px;
}
.sequenceStepIcon { color: var(--color-accent); }
.sequenceStepCount {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  line-height: 1;
}

/* ─── Prospecting approach ─── */
.approachText {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.activityLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin-top: var(--space-2);
  padding: 0;
  border: 0;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-tertiary);
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out);
}
.activityLink:hover { color: var(--color-accent); }

/* ─── Sample email ─── */
.emailBlock { display: flex; flex-direction: column; gap: var(--space-2); }
.emailPreview {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
}

</style>
